import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Package, AlertTriangle, Loader2, ArrowLeft, ShieldOff, RotateCw,
  CalendarRange, FileSpreadsheet, FileText,
} from "lucide-react";
import { SlideTTD, SlideMenipis, LiveStockTable, SlideGudang } from "@/components/barang/LiveStockSlides";

const REFRESH_MS = 15000;
const ROTASI_MS = 10000;
const SLIDE_LABELS = ["Aktivitas Terbaru", "Stok Menipis", "Tanda Terima Pending", "Stok Gudang"];
const NSLIDE = 4;
const fmtJam = (d) => d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

export default function LiveStockMonitor() {
  const [data, setData] = useState(null);
  const [periode, setPeriode] = useState("hari"); // hari | minggu | bulan | custom
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const [customOpen, setCustomOpen] = useState(false);
  const [ekspor, setEkspor] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [clock, setClock] = useState(new Date());
  const [forbidden, setForbidden] = useState(false);
  const [gagal, setGagal] = useState(false);
  const [rotasi, setRotasi] = useState(false);
  const [slide, setSlide] = useState(0); // 0 aktivitas, 1 stok menipis, 2 tanda terima pending
  const [lowStock, setLowStock] = useState([]);
  const [ttPending, setTtPending] = useState([]);
  const [gudang, setGudang] = useState([]);
  const [cariGudang, setCariGudang] = useState(false); // jeda rotasi saat mencari di slide gudang

  const rangeParams = useCallback(
    () => (periode === "custom" && dari ? { start: dari, ...(sampai ? { end: sampai } : {}) } : { periode }),
    [periode, dari, sampai]);

  const load = useCallback(() => {
    api.get("/barang/live-stock", { params: rangeParams() })
      .then((res) => {
        setData(res.data);
        setUpdatedAt(new Date());
        setForbidden(false);
        setGagal(false);
      })
      .catch((err) => {
        if (err.response?.status === 403) setForbidden(true);
        else setGagal(true);
      });
    api.get("/barang/low-stock")
      .then((res) => setLowStock([...res.data].sort((a, b) => a.stok_akhir - b.stok_akhir)))
      .catch(() => {});
    api.get("/barang/transaksi/tanpa-ttd")
      .then((res) => setTtPending(res.data || []))
      .catch(() => {});
    api.get("/barang")
      .then((res) => setGudang(res.data || []))
      .catch(() => {});
  }, [rangeParams]);

  useEffect(() => {
    setData(null);
    load();
    const iv = setInterval(load, REFRESH_MS);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    const iv = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!rotasi) return; // rotasi OFF: slide dikendalikan manual lewat tab
    if (cariGudang) return; // rotasi dijeda selama pencarian di slide gudang aktif
    const iv = setInterval(() => setSlide((s) => (s + 1) % NSLIDE), ROTASI_MS);
    return () => clearInterval(iv);
  }, [rotasi, cariGudang]);

  const downloadBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const unduhExcel = async () => {
    setEkspor(true);
    try {
      const res = await api.get("/barang/live-stock/export/excel", { params: rangeParams(), responseType: "blob" });
      downloadBlob(res.data, "live_stock_consumable.xlsx");
    } catch { toast.error("Gagal mengunduh Excel"); } finally { setEkspor(false); }
  };

  const unduhPdf = async () => {
    setEkspor(true);
    try {
      const res = await api.post("/barang/live-stock/export/pdf", { ...rangeParams(), charts: [] }, { responseType: "blob" });
      downloadBlob(res.data, "live_stock_consumable.pdf");
    } catch { toast.error("Gagal mengunduh PDF"); } finally { setEkspor(false); }
  };

  if (forbidden) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="live-stock-forbidden">
        <div className="text-center space-y-3">
          <ShieldOff className="h-10 w-10 mx-auto text-slate-300" />
          <p className="text-slate-500 text-sm">Akun Anda tidak memiliki akses modul Consumable.</p>
          <Link to="/" className="text-blue-600 text-sm underline">Kembali ke aplikasi</Link>
        </div>
      </div>
    );
  }

  const items = data?.items || [];
  const menipisCount = items.filter((b) => b.menipis).length;

  // Isi utama sesuai slide/status — early return agar tidak ada rantai ternary panjang
  const renderIsi = () => {
    if (slide === 3) return <SlideGudang gudang={gudang} onCariAktif={setCariGudang} />;
    if (slide === 2) return <SlideTTD ttPending={ttPending} />;
    if (slide === 1) return <SlideMenipis lowStock={lowStock} />;
    if (gagal && data === null) {
      return (
        <div className="py-24 text-center text-slate-500 space-y-3" data-testid="live-stock-error">
          <AlertTriangle className="h-10 w-10 mx-auto text-amber-500" />
          <p>Gagal memuat data — akan dicoba lagi otomatis tiap 15 detik.</p>
          <button type="button" onClick={load} data-testid="live-stock-retry"
            className="h-9 px-4 rounded-full text-xs font-bold border border-slate-300 bg-white text-slate-700 hover:bg-slate-100">
            Coba Lagi Sekarang
          </button>
        </div>
      );
    }
    if (data === null) {
      return (
        <div className="py-24 flex justify-center" data-testid="live-stock-loading">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <div className="py-24 text-center text-slate-400" data-testid="live-stock-empty">
          <Package className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          Belum ada transaksi barang ({data?.label || "periode ini"}).
        </div>
      );
    }
    return <LiveStockTable items={items} />;
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="live-stock-page">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white/90 px-5 py-4 sticky top-0 backdrop-blur shadow-sm">
        <img src="/logo-patra.png" alt="PATRA Facility Management" className="h-9 w-auto mb-3" data-testid="stock-monitor-logo" />
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <div className="flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
            </span>
            <div>
              <h1 className="text-lg sm:text-xl font-extrabold tracking-wide flex items-center gap-2 text-slate-900">
                <Package className="h-5 w-5 text-blue-600" /> LIVE STOCK CONSUMABLE
              </h1>
              <p className="text-[11px] text-slate-500">
                Barang bertransaksi · {data?.label || "hari ini"} · refresh otomatis 15 detik
                {updatedAt && <> · diperbarui <span className="text-emerald-600 font-semibold" data-testid="live-stock-updated">{fmtJam(updatedAt)}</span></>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button type="button" onClick={() => setRotasi((v) => !v)}
              data-testid="live-stock-rotasi-toggle"
              title="Bergantian otomatis antara Aktivitas Terbaru & Stok Menipis tiap 10 detik"
              className={`h-9 px-4 rounded-full text-xs font-bold border inline-flex items-center gap-1.5 transition-colors ${
                rotasi
                  ? "bg-emerald-600 border-emerald-600 text-white"
                  : "bg-white border-slate-300 text-slate-500 hover:text-slate-800"}`}>
              <RotateCw className={`h-3.5 w-3.5 ${rotasi ? "animate-spin [animation-duration:3s]" : ""}`} />
              Rotasi {rotasi ? "ON" : "OFF"}
            </button>
            {[{ k: "hari", label: "Hari Ini" }, { k: "minggu", label: "7 Hari" }, { k: "bulan", label: "1 Bulan" }].map((c) => (
              <button key={c.k} type="button" onClick={() => { setPeriode(c.k); setCustomOpen(false); }}
                data-testid={`live-stock-periode-${c.k}`}
                className={`h-9 px-4 rounded-full text-xs font-bold border transition-colors ${
                  periode === c.k
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "bg-white border-slate-300 text-slate-500 hover:text-slate-800"}`}>
                {c.label}
              </button>
            ))}
            <button type="button" onClick={() => setCustomOpen((v) => !v)} data-testid="live-stock-periode-custom"
              className={`h-9 px-4 rounded-full text-xs font-bold border inline-flex items-center gap-1.5 transition-colors ${
                periode === "custom"
                  ? "bg-blue-600 border-blue-600 text-white"
                  : "bg-white border-slate-300 text-slate-500 hover:text-slate-800"}`}>
              <CalendarRange className="h-3.5 w-3.5" /> Custom
            </button>
            <button type="button" onClick={unduhExcel} disabled={ekspor} data-testid="live-stock-export-excel"
              title="Unduh ringkasan periode ke Excel"
              className="h-9 px-4 rounded-full text-xs font-bold border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1.5 disabled:opacity-50">
              <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
            </button>
            <button type="button" onClick={unduhPdf} disabled={ekspor} data-testid="live-stock-export-pdf"
              title="Unduh ringkasan + grafik periode ke PDF"
              className="h-9 px-4 rounded-full text-xs font-bold border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 inline-flex items-center gap-1.5 disabled:opacity-50">
              {ekspor ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} PDF
            </button>
            <span className="font-mono text-2xl sm:text-3xl font-bold text-slate-800 tabular-nums ml-2" data-testid="live-stock-clock">
              {fmtJam(clock)}
            </span>
            <Link to="/" title="Kembali ke aplikasi" data-testid="live-stock-back"
              className="h-9 w-9 rounded-md border border-slate-300 flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </div>
        </div>
        {customOpen && (
          <div className="flex flex-wrap items-center gap-2 mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
            data-testid="live-stock-custom-row">
            <span className="text-xs font-semibold text-slate-600">Rentang:</span>
            <input type="date" value={dari} onChange={(e) => setDari(e.target.value)} data-testid="live-stock-dari"
              className="h-8 rounded-md border border-slate-300 px-2 text-sm" />
            <span className="text-slate-400 text-sm">s/d</span>
            <input type="date" value={sampai} onChange={(e) => setSampai(e.target.value)} data-testid="live-stock-sampai"
              className="h-8 rounded-md border border-slate-300 px-2 text-sm" />
            <button type="button" data-testid="live-stock-custom-apply"
              onClick={() => { if (!dari) { toast.error("Isi tanggal Dari"); return; } setPeriode("custom"); }}
              className="h-8 px-4 rounded-md text-xs font-bold bg-blue-600 text-white hover:bg-blue-700">
              Terapkan
            </button>
          </div>
        )}
        {/* Navigasi manual antar tampilan (berfungsi baik saat rotasi ON/OFF) */}
        <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid="live-stock-tabs">
          {SLIDE_LABELS.map((lbl, i) => (
            <button key={lbl} type="button" onClick={() => setSlide(i)} data-testid={`live-stock-tab-${i}`}
              className={`h-8 px-3 rounded-full text-xs font-bold border transition-colors ${
                slide === i ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-300 text-slate-500 hover:text-slate-800"}`}>
              {lbl}
            </button>
          ))}
        </div>
        {/* Ringkasan */}
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-2">
            <p className="text-[10px] uppercase tracking-wider text-blue-500">Barang Aktif</p>
            <p className="text-xl font-extrabold text-blue-700" data-testid="live-stock-count">{items.length}</p>
          </div>
          <div className={`rounded-lg px-4 py-2 border ${menipisCount > 0 ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-100"}`}>
            <p className={`text-[10px] uppercase tracking-wider ${menipisCount > 0 ? "text-rose-500" : "text-emerald-600"}`}>Stok Menipis</p>
            <p className={`text-xl font-extrabold ${menipisCount > 0 ? "text-rose-600" : "text-emerald-600"}`}
              data-testid="live-stock-menipis-count">{menipisCount}</p>
          </div>
          <div className={`rounded-lg px-4 py-2 border ${ttPending.length > 0 ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}>
            <p className={`text-[10px] uppercase tracking-wider ${ttPending.length > 0 ? "text-amber-600" : "text-slate-400"}`}>TT Pending</p>
            <p className={`text-xl font-extrabold ${ttPending.length > 0 ? "text-amber-600" : "text-slate-400"}`}
              data-testid="live-stock-ttd-count">{ttPending.length}</p>
          </div>
          <div className="ml-auto flex items-center gap-2" data-testid="live-stock-slide-indicator">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
              {SLIDE_LABELS[slide]}
            </span>
            {[0, 1, 2, 3].map((i) => (
              <button key={SLIDE_LABELS[i]} type="button" onClick={() => setSlide(i)} title={SLIDE_LABELS[i]}
                data-testid={`live-stock-dot-${i}`}
                className={`h-3 w-3 rounded-full transition-colors ${slide === i ? "bg-emerald-500" : "bg-slate-300 hover:bg-slate-400"}`} />
            ))}
          </div>
        </div>
      </div>

      {/* Isi */}
      <div className="p-5">{renderIsi()}</div>
    </div>
  );
}
